/**
 * Sentry Replay "Hydration Error" issues (JAVASCRIPT-NEXTJS-69) on /embed/*
 * are not classic SSR/client markup mismatches. Attached replays all show a
 * Next.js ChunkLoadError (failed page/layout chunk) a few ms before
 * `replay.hydrate-error`. Without the client bundle, React cannot hydrate
 * the server HTML, and Replay reports that as a hydration failure.
 *
 * These helpers stay pure so focused tests can cover the drop rules without
 * the Sentry SDK.
 */

export interface HydrationNoiseInput {
    message?: string | null;
    title?: string | null;
    url?: string | null;
    breadcrumbs?: Array<{
        category?: string | null;
        message?: string | null;
    }> | null;
}

function combinedText(input: HydrationNoiseInput): string {
    const breadcrumbText = (input.breadcrumbs || [])
        .map((breadcrumb) => `${breadcrumb.category || ""} ${breadcrumb.message || ""}`)
        .join(" ");
    return `${input.message || ""} ${input.title || ""} ${breadcrumbText}`.toLowerCase();
}

export function isHydrationErrorSignal(input: HydrationNoiseInput): boolean {
    const text = combinedText(input);
    return (
        text.includes("hydration error") ||
        (input.breadcrumbs || []).some(
            (breadcrumb) => (breadcrumb.category || "").toLowerCase() === "replay.hydrate-error",
        )
    );
}

export function hasChunkLoadFailureSignal(input: HydrationNoiseInput): boolean {
    const text = combinedText(input);
    return (
        text.includes("chunkloaderror") ||
        (text.includes("loading chunk") && (text.includes("failed") || text.includes("timeout"))) ||
        text.includes("loading css chunk")
    );
}

/**
 * Drop Replay hydration events that are really a failed Next.js chunk, or
 * that fire on public embed routes where Replay should not run.
 */
export function shouldIgnoreReplayHydrationNoise(input: HydrationNoiseInput): boolean {
    if (!isHydrationErrorSignal(input)) return false;
    if (hasChunkLoadFailureSignal(input)) return true;

    const url = (input.url || "").toLowerCase();
    return url.includes("/embed/") || url.endsWith("/embed");
}
